// ------------------------------------------------------------
// AI Attribution — per Loren Data AI Use Policy §8.2
// Tool:        Claude Code (Anthropic)
// 2026-07-15: Added convert-to-contract state and removeTenant action (WI9896 Phase 4) - SangPham
// ------------------------------------------------------------
/**
 * DeveloperPlanPage Store
 * Zustand store for managing developer plan tenants state
 */

import { create } from 'zustand';
import type { DeveloperPlanTenant, DeveloperPlanTenantsResponse } from '@/application/queries/developerPlan';

/**
 * Initial state values
 */
const initialState = {
  data: null as DeveloperPlanTenantsResponse | null,
  loading: true,
  error: null as Error | null,
  selectedTenant: null as DeveloperPlanTenant | null,
  sheetOpen: false,
  extending: false,
  extendError: null as Error | null,
  extendSuccess: false,
  syncing: false,
  syncError: null as Error | null,
  converting: false,
  convertError: null as Error | null,
  convertSuccess: false,
};

/**
 * Store state interface
 */
interface DeveloperPlanState {
  // Data
  data: DeveloperPlanTenantsResponse | null;
  loading: boolean;
  error: Error | null;

  // Sheet state
  selectedTenant: DeveloperPlanTenant | null;
  sheetOpen: boolean;

  // Extend trial state
  extending: boolean;
  extendError: Error | null;
  extendSuccess: boolean;

  // Sync state
  syncing: boolean;
  syncError: Error | null;

  // Convert to contract state
  converting: boolean;
  convertError: Error | null;
  convertSuccess: boolean;

  // Actions
  setData: (data: DeveloperPlanTenantsResponse) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  openSheet: (tenant: DeveloperPlanTenant) => void;
  closeSheet: () => void;
  setExtending: (extending: boolean) => void;
  setExtendError: (error: Error | null) => void;
  updateTenantTrialDate: (networkId: number, newTrialEndDate: string) => void;
  setExtendSuccess: (success: boolean) => void;
  setSyncing: (syncing: boolean) => void;
  setSyncError: (error: Error | null) => void;
  setConverting: (converting: boolean) => void;
  setConvertError: (error: Error | null) => void;
  setConvertSuccess: (success: boolean) => void;
  removeTenant: (networkId: number) => void;
  reset: () => void;

  // For testing
  _reset: () => void;
}

/**
 * Zustand store for DeveloperPlanPage
 */
export const useDeveloperPlanStore = create<DeveloperPlanState>((set) => ({
  // Initial state
  ...initialState,

  // Actions
  setData: (data) =>
    set({
      data,
      loading: false,
      error: null,
    }),

  setLoading: (loading) => set({ loading }),

  setError: (error) =>
    set({
      error,
      loading: false,
    }),

  openSheet: (tenant) =>
    set({
      selectedTenant: tenant,
      sheetOpen: true,
      extendError: null,
      extendSuccess: false,
    }),

  closeSheet: () =>
    set({
      selectedTenant: null,
      sheetOpen: false,
      extendError: null,
      extendSuccess: false,
    }),

  setExtending: (extending) => set({ extending }),

  setExtendError: (extendError) =>
    set({
      extendError,
      extending: false,
    }),

  updateTenantTrialDate: (networkId, newTrialEndDate) =>
    set((state) => {
      if (!state.data) return state;
      const updatedTenants = state.data.tenants.map((t) =>
        t.networkId === networkId
          ? { ...t, expireDate: newTrialEndDate, trialExpireDate: newTrialEndDate }
          : t
      );
      return {
        data: {
          ...state.data,
          tenants: updatedTenants,
        },
        // Also update selectedTenant so the sheet reflects the new date
        selectedTenant:
          state.selectedTenant?.networkId === networkId
            ? { ...state.selectedTenant, expireDate: newTrialEndDate, trialExpireDate: newTrialEndDate }
            : state.selectedTenant,
      };
    }),

  setExtendSuccess: (extendSuccess) =>
    set({
      extendSuccess,
      extending: false,
    }),

  setSyncing: (syncing) => set({ syncing }),

  setSyncError: (syncError) => set({ syncError }),

  setConverting: (converting) => set({ converting }),

  setConvertError: (convertError) =>
    set({
      convertError,
      converting: false,
    }),

  setConvertSuccess: (convertSuccess) =>
    set({
      convertSuccess,
      converting: false,
    }),

  // Graduated networks drop out of the Developer Plan list — remove the row
  // in-place (no full refetch) and close the sheet if it was showing this tenant.
  removeTenant: (networkId) =>
    set((state) => {
      if (!state.data) return state;
      return {
        data: {
          ...state.data,
          tenants: state.data.tenants.filter((t) => t.networkId !== networkId),
        },
        sheetOpen: state.selectedTenant?.networkId === networkId ? false : state.sheetOpen,
        selectedTenant: state.selectedTenant?.networkId === networkId ? null : state.selectedTenant,
      };
    }),

  reset: () =>
    set({
      ...initialState,
      loading: true,
    }),

  // Reset to initial state (for testing)
  _reset: () => set(initialState),
}));
